import { Spinner } from '@change/ds-core-ui';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import type { DecisionMakerProfilePagePrefetchedDataProps } from 'src/app/pages/decisionMakerProfile/pageContext';
import {
	DecisionMakerProfilePageContextProvider,
	prefetchPageContext,
} from 'src/app/pages/decisionMakerProfile/pageContext';
import { addSchemeToUrl } from 'src/app/shared/utils/assets';
import { isLoaded } from 'src/app/shared/utils/async';

import { Layout } from '../layout';

import { DecisionMakerProfileErrorHandler } from './ErrorHandler';

export const DecisionMakerProfile = withPrefetchedData(
	function DecisionMakerProfile({ prefetchedData }: DecisionMakerProfilePagePrefetchedDataProps) {
		const { slug } = useKnownAvailableParams<{ slug: string }>();

		return (
			<DecisionMakerProfilePageContextProvider
				slug={slug}
				prefetchedData={prefetchedData}
				errorHandler={DecisionMakerProfileErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</DecisionMakerProfilePageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ prefetchedData, i18n, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { title: role } = prefetchedData.pageData.pageData;
			return fromTemplate(
				`${i18n.translate('corgi.decision_maker_profile.meta.page_title', {
					display_name: prefetchedData.pageData.pageData.displayName,
				})}${role ? ` - ${role}` : ''}`,
			);
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { slug, id } = prefetchedData.pageData.pageData;
			return `/decision-makers/${slug || id}`;
		},
		pageMetadata: ({ prefetchedData, pageTitle, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { description, photo, displayName, title: role } = prefetchedData.pageData.pageData;
			const slicedDescription = description ? description.slice(0, 150) : undefined;
			const name = i18n.translate('corgi.decision_maker_profile.meta.description.name_label', {
				display_name: displayName,
			});
			const dmTitleIfExists = role
				? i18n.translate('corgi.decision_maker_profile.meta.description.title_label', { dm_title: role })
				: '';
			const pageSummary = i18n.translate('corgi.decision_maker_profile.meta.description.page_summary');
			const searchFriendlyDescription = [name, dmTitleIfExists, pageSummary].filter(Boolean).join('. ');
			const image = addSchemeToUrl(photo?.userLarge.url ?? 'https://static.change.org/images/default-petition.gif');
			return {
				description: searchFriendlyDescription,
				ogDescription: slicedDescription,
				ogImage: image,
				ogTitle: displayName,
				ogType: 'website',
				twitterImage: image,
				twitterImageAlt: pageTitle,
			};
		},
		pageSchemaOrg: ({ prefetchedData, definePerson, defineAddress }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { description, displayName, id, photo, title: jobTitle, slug, state } = prefetchedData.pageData.pageData;
			const image = photo?.userLarge.url ? addSchemeToUrl(photo.userLarge.url) : null;
			return definePerson({
				name: displayName,
				url: `/decision-makers/${slug || id}`,
				description: description ? description.slice(0, 150) : undefined,
				...(image && { image }),
				...(jobTitle && { jobTitle }),
				...(state && {
					address: defineAddress({
						addressRegion: state,
						addressCountry: 'US',
					}),
				}),
			});
		},
	},
);
